import { useEffect, useState } from 'react'
import { useAuthStore } from './store/authStore'
import { useWebSocket } from './hooks/useWebSocket'
import LoginView    from './views/LoginView'
import DashboardView from './views/DashboardView'
import NavSidebar   from './components/NavSidebar'
import TopBar       from './components/TopBar'
import DevicePanel  from './components/DevicePanel'
import ChatPanel    from './components/ChatPanel'
import PttPanel     from './components/PttPanel'
import MonitorView   from './views/MonitorView'
import MapView       from './views/MapView'
import LPRSearchView from './views/LPRSearchView'
import SettingsView  from './views/SettingsView'
import ReportsView   from './views/ReportsView'
import AuditView     from './views/AuditView'
import DetectionsView from './views/DetectionsView'
import GuardView     from './views/GuardView'
import EdgeHealthView from './views/EdgeHealthView'
import SiteConfigView from './views/SiteConfigView'
import AnalysisView   from './views/AnalysisView'
import IncidentsView  from './views/IncidentsView'
import ErrorBoundary from './components/ErrorBoundary'
import IncidentDetailPanel from './components/IncidentDetailPanel'

// Traducción condicional de títulos por host. cad.retaliq.net = Cement India
// demo en inglés; los demás tenants (Copec, Hites, Quipasur) siguen en español.
const _isEN = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'

const VIEW_TITLES = _isEN ? {
  dashboard:  ['Dashboard', 'Network overview'],
  map:        ['Network Map', 'Real-time status of all sites'],
  monitor:    ['Live Monitor', 'Real-time cameras and alerts'],
  incidents:  ['Incidents', 'Active incident management'],
  lpr:        ['ANPR Search', 'License plate detection history'],
  detections: ['Detections', 'Unified feed of people and vehicles'],
  persons:    ['People', 'Face recognition and enrollment'],
  vehicles:   ['Vehicles', 'ANPR and vehicle detections'],
  reports:    ['Reports', 'KPIs and automatic shift reports'],
  audit:      ['Audit', 'Operator activity and response metrics'],
  settings:   ['Settings', 'System administration'],
  edgehealth: ['Edge Health', 'Live status of each deployed edge node'],
  siteconfig: ['Site Configuration', 'Floor plan + cameras + analytics'],
  analysis:   ['Floor Analysis', 'Heatmap and circulation lines per floor'],
} : {
  dashboard:  ['Dashboard', 'Visión general de la red'],
  map:        ['Mapa de red', 'Estado de todos los sitios en tiempo real'],
  monitor:    ['Monitor en vivo', 'Cámaras y alertas en tiempo real'],
  incidents:  ['Incidentes', 'Gestión de incidentes activos'],
  lpr:        ['Búsqueda LPR', 'Historial de detecciones de patentes'],
  detections: ['Detecciones', 'Feed unificado de personas y vehículos'],
  persons:    ['Personas', 'Reconocimiento facial y enrollment'],
  vehicles:   ['Vehículos', 'LPR y detecciones vehiculares'],
  reports:    ['Reportes', 'KPIs y reportes de turno automáticos'],
  audit:      ['Auditoría', 'Actividad de operadores y métricas de respuesta'],
  settings:   ['Configuración', 'Administración del sistema'],
  edgehealth: ['Salud de Edge', 'Estado en vivo de cada M4 desplegado'],
  siteconfig: ['Configuración del sitio', 'Plano + cámaras + analíticas'],
  analysis:   ['Análisis del piso', 'Mapa de calor y líneas de circulación por piso'],
}

// Mapeo path → view para resolver la URL al cargar la app. La galería
// forense abre `/monitor?tab=playback&...` en una pestaña nueva; el primer
// cuadrante del mapa abre `/monitor?site=…` para volver al vivo. Sin esto,
// la app siempre arrancaba en dashboard ignorando la URL.
const PATH_TO_VIEW = {
  '/'           : 'dashboard',
  '/dashboard'  : 'dashboard',
  '/map'        : 'map',
  '/monitor'    : 'monitor',
  '/incidents'  : 'incidents',
  '/lpr'        : 'lpr',
  '/detections' : 'detections',
  '/persons'    : 'persons',
  '/vehicles'   : 'vehicles',
  '/reports'    : 'reports',
  '/audit'      : 'audit',
  '/settings'   : 'settings',
  '/edge-health': 'edgehealth',
  '/site-config': 'siteconfig',
  '/analysis'   : 'analysis',
}

function viewFromPath() {
  const p = window.location.pathname.replace(/\/$/, '') || '/'
  return PATH_TO_VIEW[p] || 'dashboard'
}

function Placeholder({ view }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🚧</div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{VIEW_TITLES[view]?.[0] || view}</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>En construcción</div>
      </div>
    </div>
  )
}

export default function App() {
  const user    = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  const refresh = useAuthStore(s => s.refresh)

  const [view,           setView]     = useState(viewFromPath)
  const [devicePanel,    setDevPanel] = useState(false)
  const [incidents,      setInc]      = useState([])
  const [lastWsMsg,      setLastWsMsg] = useState(null)
  const [chatOpen,       setChatOpen]  = useState(false)
  const [pttOpen,        setPttOpen]   = useState(false)
  const [pttCallTarget,  setPttTarget] = useState(null)  // { id, name } de sitio a llamar
  const [openIncId,      setOpenIncId] = useState(null)  // incidente abierto en el drawer derecho

  useEffect(() => { refresh() }, [])

  // Reaccionar al botón "Atrás" del browser. Si el usuario navega con
  // history.back, sincronizamos la view con el nuevo pathname.
  useEffect(() => {
    const onPop = () => setView(viewFromPath())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Bus global para abrir el drawer del Caso unificado desde cualquier
  // lugar (MapView IncidentCard, ChatPanel deep-link, toasts del SW push).
  // Disparar con: window.dispatchEvent(new CustomEvent('open-incident', { detail: { id: 42 } }))
  useEffect(() => {
    const onOpen = (e) => {
      const id = e?.detail?.id
      if (id) setOpenIncId(id)
    }
    window.addEventListener('open-incident', onOpen)
    return () => window.removeEventListener('open-incident', onOpen)
  }, [])

  // Cuando el usuario cambia de view por el sidebar, escribimos la URL
  // (sin recargar) para que F5 mantenga la view y los deep-links sigan
  // funcionando. Preservamos query string si el path no cambió de raíz.
  const navigateTo = (v) => {
    const targetPath = '/' + (v === 'dashboard' ? '' : v)
    if (window.location.pathname !== targetPath) {
      // Click del sidebar limpia query — ya no aplica al cambiar de pantalla.
      window.history.pushState({}, '', targetPath)
    }
    setView(v)
  }

  useWebSocket((msg) => {
    setLastWsMsg(msg)
    if (msg.type === 'incident_created') {
      setInc(prev => [msg.incident, ...prev])
    }
    if (msg.type === 'incident_updated') {
      setInc(prev => prev.map(i => i.id === msg.incident.id ? msg.incident : i))
    }
  })

  // Antes abría un popup `/incident/N` que no tiene ruta y caía al dashboard.
  // Ahora monta el IncidentDetailPanel como drawer derecho sobre la view actual,
  // sin cambiar de pantalla — funciona desde Dashboard, Map, WS toasts, etc.
  const openIncident = (inc) => {
    const id = typeof inc === 'object' ? inc?.id : inc
    if (id) setOpenIncId(id)
  }

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-root)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cargando…</span>
    </div>
  )

  if (!user) return <LoginView />

  // Shell condicional según el rol del usuario logueado.
  //
  // guard → PWA móvil dedicada con su propio flujo (login + dashboard +
  // incidentes + PTT + reporte + video llamada). No ve sidebar ni el shell
  // operador. Auth comparte el mismo /auth/login y JWT.
  //
  // Los demás roles (admin, supervisor, operator, analyst, superadmin)
  // ven el CAD completo con sidebar + topbar + main.
  if (user.rol === 'guard') {
    return (
      <ErrorBoundary view="Guardia" inline>
        <GuardView />
      </ErrorBoundary>
    )
  }

  const [title, subtitle] = VIEW_TITLES[view] || [view, '']
  const openIncidents = incidents.filter(i => ['open','acknowledged','in_progress'].includes(i.status))

  return (
    <ErrorBoundary>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar
          title={title} subtitle={subtitle} alerts={openIncidents}
          onChatToggle={() => setChatOpen(p => !p)}
          chatOpen={chatOpen}
        />
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <NavSidebar
          active={view}
          onNav={(v) => {
            if (v === 'ptt') { setPttOpen(p => !p); return }
            navigateTo(v)
          }}
          pttActive={pttOpen}
        />
        <PttPanel
          open={pttOpen}
          onClose={() => setPttOpen(false)}
          callTarget={pttCallTarget}
          onCallHandled={() => setPttTarget(null)}
        />
          <main style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
            <ErrorBoundary view="Dashboard" inline>
              {view === 'dashboard' && <DashboardView onOpenIncident={openIncident} />}
            </ErrorBoundary>
            <ErrorBoundary view="Monitor" inline>
              {view === 'monitor' && <MonitorView wsMessage={lastWsMsg} />}
            </ErrorBoundary>
            <ErrorBoundary view="Mapa" inline>
              {view === 'map' && (
                <MapView
                  wsMessage={lastWsMsg}
                  onCallSite={(site) => {
                    setPttTarget({ id: site.id, name: site.name })
                    setPttOpen(true)
                  }}
                />
              )}
            </ErrorBoundary>
            <ErrorBoundary view="LPR" inline>
              {view === 'lpr' && <LPRSearchView />}
            </ErrorBoundary>
            <ErrorBoundary view="Configuración" inline>
              {view === 'settings' && <SettingsView />}
            </ErrorBoundary>
            <ErrorBoundary view="Reportes" inline>
              {view === 'reports' && <ReportsView />}
            </ErrorBoundary>
            <ErrorBoundary view="Auditoría" inline>
              {view === 'audit' && <AuditView />}
            </ErrorBoundary>
            <ErrorBoundary view="Detecciones" inline>
              {view === 'detections' && <DetectionsView />}
            </ErrorBoundary>
            <ErrorBoundary view="Edge / Sitio" inline>
              {view === 'edgehealth' && <EdgeHealthView />}
              {view === 'siteconfig' && <SiteConfigView />}
            </ErrorBoundary>
            <ErrorBoundary view="Análisis" inline>
              {view === 'analysis' && <AnalysisView />}
            </ErrorBoundary>
            <ErrorBoundary view="Incidentes" inline>
              {view === 'incidents' && <IncidentsView />}
            </ErrorBoundary>
            {!['dashboard','monitor','map','lpr','settings','reports','audit','detections','edgehealth','siteconfig','analysis','incidents'].includes(view) && <Placeholder view={view} />}
          </main>
          <ChatPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            context={{ view, site_name: null }}
          />
          <DevicePanel
            collapsed={!devicePanel}
            onToggle={() => setDevPanel(p => !p)}
            onCameraClick={(cam, fullscreen) => {
              if (fullscreen) {
                window.open(`/camera/${cam.id}`, `cam_${cam.id}`, 'width=1280,height=720,resizable=yes')
              }
            }}
          />
        </div>
        {/* Drawer derecho — Caso unificado. Aparece sobre la view actual
            sin cambiar de pantalla. Reutilizado desde Dashboard, Map, WS toasts. */}
        {openIncId && (
          <>
            <div onClick={() => setOpenIncId(null)} style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              zIndex: 90, animation: 'fadeIn 0.15s ease',
            }} />
            <div style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: 'min(480px, 92vw)', zIndex: 91,
              boxShadow: '-4px 0 20px rgba(0,0,0,0.4)',
            }}>
              <ErrorBoundary view="Detalle incidente" inline>
                <IncidentDetailPanel
                  incidentId={openIncId}
                  onClose={() => setOpenIncId(null)}
                  onUpdated={(updated) => {
                    if (updated?.id) {
                      setInc(prev => prev.map(i => i.id === updated.id ? updated : i))
                    }
                  }}
                />
              </ErrorBoundary>
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  )
}
